import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ApiClientService } from '../../core/api/api-client.service';
import {
  CreateDriverRequest,
  DriverListResponse,
  DriverGroupOption,
  DriverStatus,
  DriverTrashItem,
  DriverSummary,
  EnrollmentStatus
} from '../../core/models/portal.models';

type DriverFormState = Omit<CreateDriverRequest, 'companyId'> & {
  companyId: number | null;
};

const EMPTY_DRIVER_FORM: DriverFormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  personnelNumber: '',
  companyId: null,
  groupIds: [],
  preferredLanguage: 'de',
  dateOfBirth: null,
  licenseValidUntil: null,
  pScheinValidUntil: null,
  statusValidUntil: null,
  status: 'ACTIVE'
};

@Component({
  selector: 'app-drivers-page',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './drivers-page.component.html'
})
export class DriversPageComponent implements OnInit {
  private readonly api = inject(ApiClientService);
  private readonly route = inject(ActivatedRoute);

  protected readonly overview = signal<DriverListResponse | null>(null);
  protected readonly companies = computed(() => this.overview()?.companies ?? []);
  protected readonly driverGroups = signal<DriverGroupOption[]>([]);
  protected readonly availableDriverGroups = computed(() => {
    const selectedCompanyId = this.driverForm().companyId;
    return this.driverGroups().filter((group) => group.companyId === selectedCompanyId);
  });
  protected readonly search = signal('');
  protected readonly companyFilter = signal('');
  protected readonly statusFilter = signal('');
  protected readonly notice = signal<string | null>(null);
  protected readonly createDriverDialogOpen = signal(false);
  protected readonly trashDialogOpen = signal(false);
  protected readonly deletedDrivers = signal<DriverTrashItem[]>([]);
  protected readonly driverForm = signal<DriverFormState>(this.emptyDriverForm());
  protected readonly driverSaving = signal(false);
  protected readonly driverCreateError = signal<string | null>(null);

  ngOnInit(): void {
    this.route.queryParamMap.subscribe((params) => {
      this.statusFilter.set(params.get('status') ?? '');
      this.load();
    });
  }

  protected load(): void {
    this.api
      .drivers(0, 100, this.search(), this.companyFilter(), this.statusFilter())
      .subscribe((overview) => this.overview.set(overview));
  }

  protected applyFilters(): void {
    this.notice.set(null);
    this.load();
  }

  protected resetFilters(): void {
    this.search.set('');
    this.companyFilter.set('');
    this.statusFilter.set('');
    this.notice.set(null);
    this.load();
  }

  protected updateSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value);
  }

  protected updateCompanyFilter(event: Event): void {
    this.companyFilter.set((event.target as HTMLSelectElement).value);
  }

  protected updateStatusFilter(event: Event): void {
    this.statusFilter.set((event.target as HTMLSelectElement).value);
  }

  protected openCreateDriverDialog(): void {
    const selectedCompanyId = this.companyFilter()
      ? Number(this.companyFilter())
      : this.companies()[0]?.id ?? null;
    this.driverCreateError.set(null);
    this.driverForm.set({ ...this.emptyDriverForm(), companyId: selectedCompanyId });
    this.loadDriverGroups();
    this.createDriverDialogOpen.set(true);
  }

  protected closeCreateDriverDialog(): void {
    if (this.driverSaving()) {
      return;
    }
    this.createDriverDialogOpen.set(false);
    this.driverCreateError.set(null);
  }

  protected openTrashDialog(): void {
    this.trashDialogOpen.set(true);
    this.loadTrash();
  }

  protected closeTrashDialog(): void {
    this.trashDialogOpen.set(false);
  }

  protected loadTrash(): void {
    this.api.deletedDrivers(0, 100).subscribe((trash) => this.deletedDrivers.set(trash.items));
  }

  protected restoreDriver(driver: DriverTrashItem): void {
    this.api.restoreDriver(driver.id).subscribe(() => {
      this.notice.set('Der Fahrer wurde wiederhergestellt.');
      this.loadTrash();
      this.load();
    });
  }

  protected createDriver(event: Event): void {
    event.preventDefault();
    const request = this.normalizedDriverRequest();

    if (!request.firstName) {
      this.driverCreateError.set('Bitte gib einen Vornamen ein.');
      return;
    }

    if (!request.lastName) {
      this.driverCreateError.set('Bitte gib einen Nachnamen ein.');
      return;
    }

    if (!request.email) {
      this.driverCreateError.set('Bitte gib eine E-Mail-Adresse ein.');
      return;
    }

    if (!request.companyId) {
      this.driverCreateError.set('Bitte wähle einen Betrieb aus.');
      return;
    }

    this.driverSaving.set(true);
    this.driverCreateError.set(null);

    this.api.createDriver(request).subscribe({
      next: () => {
        this.driverSaving.set(false);
        this.createDriverDialogOpen.set(false);
        this.notice.set('Der Fahrer wurde angelegt.');
        this.load();
      },
      error: () => {
        this.driverSaving.set(false);
        this.driverCreateError.set('Der Fahrer konnte nicht angelegt werden.');
      }
    });
  }

  protected updateDriverForm(field: keyof Omit<DriverFormState, 'companyId'>, event: Event): void {
    const value = (event.target as HTMLInputElement | HTMLSelectElement).value;
    this.driverForm.update((current) => ({ ...current, [field]: value }));
  }

  protected updateDriverCompany(event: Event): void {
    const raw = (event.target as HTMLSelectElement).value;
    this.driverForm.update((current) => ({ ...current, companyId: raw ? Number(raw) : null, groupIds: [] }));
  }

  protected updateDriverGroups(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const groupIds = Array.from(select.selectedOptions)
      .map((option) => Number(option.value))
      .filter((value) => Number.isFinite(value));
    this.driverForm.update((current) => ({ ...current, groupIds }));
  }

  protected showNotice(message: string): void {
    this.notice.set(message);
  }

  protected portalId(driver: DriverSummary): string {
    return `----${driver.personnelNumber ?? `DMA-${driver.id}`}`;
  }

  protected valueOrDash(value: string | null): string {
    return value?.trim() ? value : '-';
  }

  protected driverInitials(driver: DriverSummary): string {
    const parts = driver.displayName.trim().split(/\s+/).filter(Boolean);

    if (!parts.length) {
      return 'DM';
    }

    if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }

    return parts
      .slice(0, 2)
      .map((part) => part[0] ?? '')
      .join('')
      .toUpperCase();
  }

  protected dateOrMissing(value: string | null): string {
    return value?.trim() ? value : 'nicht hinterlegt';
  }

  protected formatDate(value: string): string {
    return new Intl.DateTimeFormat('de-DE', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
  }

  protected statusLabel(status: DriverStatus): string {
    const labels: Record<DriverStatus, string> = {
      ACTIVE: 'Aktiv',
      INACTIVE: 'Inaktiv',
      TRAINING_IN_PROGRESS: 'In Schulung',
      READY: 'Einsatzbereit',
      RETRAINING_REQUIRED: 'Nachschulung erforderlich',
      OVERDUE: 'Überfällig',
      BLOCKED: 'Gesperrt'
    };

    return labels[status] ?? status;
  }

  protected enrollmentStatusLabel(status: EnrollmentStatus): string {
    const labels: Record<EnrollmentStatus, string> = {
      ASSIGNED: 'Zugewiesen',
      STARTED: 'Gestartet',
      IN_PROGRESS: 'In Bearbeitung',
      COMPLETED: 'Abgeschlossen',
      OVERDUE: 'Überfällig',
      FAILED: 'Nicht bestanden',
      RETRAINING_REQUIRED: 'Nachschulung erforderlich'
    };

    return labels[status] ?? status;
  }

  private normalizedDriverRequest(): CreateDriverRequest {
    const current = this.driverForm();
    return {
      firstName: this.clean(current.firstName) ?? '',
      lastName: this.clean(current.lastName) ?? '',
      email: this.clean(current.email)?.toLowerCase() ?? '',
      phone: this.clean(current.phone),
      personnelNumber: this.clean(current.personnelNumber),
      companyId: current.companyId ?? 0,
      groupIds: current.groupIds,
      preferredLanguage: this.clean(current.preferredLanguage) ?? 'de',
      dateOfBirth: this.clean(current.dateOfBirth),
      licenseValidUntil: this.clean(current.licenseValidUntil),
      pScheinValidUntil: this.clean(current.pScheinValidUntil),
    };
  }

  private emptyDriverForm(): DriverFormState {
    return { ...EMPTY_DRIVER_FORM };
  }

  private loadDriverGroups(): void {
    this.api.driverGroups().subscribe((groups) => this.driverGroups.set(groups));
  }

  private clean(value: string | null | undefined): string | null {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
  }
}
